import { Box, Button } from '@chakra-ui/react'
import { faCheck } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { type CertificationRequestType, CertificationStatus, TopicType } from '@/queries/constants'
import { useUser } from '@/client/context/user'
import { getCertificationUrl } from '@/client/services/url'

import RequireLoginAndApproval from '@/components/Auth/RequireLoginAndApproval'
import ShwLink from '@/components/ShwLink'
import Tippy from '@/components/UI/Tippy'

export default function TutorCertificationButton({
  width = 'auto',
  inverted = false,
  topicType,
  topicId,
  certProfileStatus,
  topicCertStatus,
  certificationTypes,
}: {
  width?: string
  inverted?: boolean
  topicType: TopicType | null
  topicId: number
  certProfileStatus: CertificationStatus
  topicCertStatus: CertificationStatus
  certificationTypes: CertificationRequestType[]
}): JSX.Element {
  const user = useUser()

  const getCertifiedText = `Get Certified (+${topicType === TopicType.challenge ? 50 : 10} SP)`
  const ineligibleText = `This topic is not eligible for certification.${
    topicType === TopicType.qa
      ? ' If you have the Course Challenge certification, you can tutor in this topic.'
      : topicType === TopicType.other
        ? ' If you have any certification, you can tutor in this topic.'
        : ''
  }`

  if (user.isLoggedOut) {
    // Don't make this a link for logged-out users, so that SEO crawlers don't look at it.
    return (
      <Tippy
        disabled={certificationTypes.length > 0}
        content="This topic is not eligible for certification."
      >
        <Box>
          <RequireLoginAndApproval>
            <Button width={width} variant={inverted ? 'ghost' : 'solid'}>
              {getCertifiedText}
            </Button>
          </RequireLoginAndApproval>
        </Box>
      </Tippy>
    )
  }

  if (certProfileStatus === CertificationStatus.None) {
    return (
      // User must create certification profile
      <Tippy disabled={certificationTypes.length > 0} content={ineligibleText}>
        <ShwLink href={`${getCertificationUrl('profile')}?redirectTopic=${topicId}`}>
          <RequireLoginAndApproval>
            <Button
              width={width}
              variant={inverted ? 'ghost' : 'solid'}
              isDisabled={certificationTypes.length === 0}
            >
              {getCertifiedText}
            </Button>
          </RequireLoginAndApproval>
        </ShwLink>
      </Tippy>
    )
  }

  // Now, we can assume the user has a certification profile
  if (topicCertStatus === CertificationStatus.None) {
    return (
      <Tippy
        content={
          certificationTypes.length === 0 ? ineligibleText : 'Get certified to tutor in this topic'
        }
      >
        <ShwLink href={`/topic/${topicId}/certify`}>
          <RequireLoginAndApproval>
            <Button
              width={width}
              variant={inverted ? 'ghost' : 'solid'}
              isDisabled={certificationTypes.length === 0}
            >
              {getCertifiedText}
            </Button>
          </RequireLoginAndApproval>
        </ShwLink>
      </Tippy>
    )
  }

  if (topicCertStatus === CertificationStatus.Pending) {
    return (
      <Tippy content="Your certification is in review">
        <RequireLoginAndApproval>
          <Button width={width} variant="solid" isDisabled>
            {getCertifiedText}
          </Button>
        </RequireLoginAndApproval>
      </Tippy>
    )
  }

  if (topicCertStatus === CertificationStatus.NotReadyForReview) {
    return (
      <Tippy content="Complete your peer reviews for your certification be reviewed">
        <ShwLink href={`/topic/${topicId}/certify`}>
          <RequireLoginAndApproval>
            <Button width={width} variant="solid">
              Finish Peer Reviews
            </Button>
          </RequireLoginAndApproval>
        </ShwLink>
      </Tippy>
    )
  }

  // Now, we can assume the user is certified

  return (
    <Tippy content="Your certification has been approved!">
      <Button
        width={width}
        variant="solid"
        leftIcon={<FontAwesomeIcon icon={faCheck} />}
        isDisabled
      >
        Certified
      </Button>
    </Tippy>
  )
}
